import React, { useEffect, useState } from "react";
import { FileDown, Lock, CalendarDays, Send } from "lucide-react";
import { api, stageClass } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const PERIODS = [["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]];
const today = () => new Date().toLocaleDateString("en-CA");

export default function WorkReport() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("daily");
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [other, setOther] = useState("");
  const [blockers, setBlockers] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [rc, setRc] = useState(null);
  const allowed = ["bdm", "admissions_officer"].includes(user?.role);
  const loadRc = () => api.get("/work-report/recipients").then(r => setRc(r.data)).catch(() => {});
  useEffect(() => { if (allowed) { loadRc(); } }, [allowed]); // eslint-disable-line

  const sendReport = async () => {
    setSending(true);
    try {
      const r = await api.post("/work-report/send", { period, date, other_tasks: other, blockers });
      const failed = [...r.data.to, ...r.data.cc].filter(x => !r.data.delivered_to.includes(x));
      toast.success(`Sent to ${r.data.delivered_to.join(", ")}`);
      if (failed.length) { toast.error(`Could not deliver to ${failed.join(", ")}`); }
      loadRc();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the report"); }
    finally { setSending(false); }
  };

  useEffect(() => { document.title = "Work Report · Admissio"; }, []);
  useEffect(() => {
    if (!allowed || !date) { return; }
    api.get("/work-report/preview", { params: { period, date } }).then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "Could not load report"));
  }, [period, date, allowed]);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.post("/work-report/pdf", { period, date, other_tasks: other, blockers }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const el = document.createElement("a");
      const cd = r.headers?.["content-disposition"] || "";
      el.href = url; el.download = cd.match(/filename="([^"]+)"/)?.[1] || `${period}-report-${date}.pdf`; el.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Could not generate the PDF"); }
    finally { setBusy(false); }
  };

  if (!allowed) return <div data-testid="work-report-forbidden" className="text-slate-500">Work reports are available to the BDM and Admissions Officer only.</div>;

  return (
    <div className="space-y-5 max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="heading-font text-3xl font-bold text-slate-900">Work Report</h1>
          <p className="text-sm text-slate-500 mt-1">Application data comes straight from the CRM. Add what else you did and any blockers, then download the PDF or send it — all reports in a month go in one email thread.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" data-testid="work-report-download" onClick={download} disabled={busy || !data}>
            <FileDown className="w-4 h-4 mr-2" />{busy ? "Generating…" : "Download PDF"}
          </Button>
          <Button data-testid="work-report-send" onClick={sendReport} disabled={sending || !data || !rc} className="bg-orange-500 hover:bg-orange-600 text-white">
            <Send className="w-4 h-4 mr-2" />{sending ? "Sending…" : rc && !rc.cc.length && rc.to[0] === rc.director_email ? "Send to Director" : "Send to Manager"}
          </Button>
        </div>
      </div>
      {rc && (
        <div data-testid="work-report-recipients" className="-mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          <span>To <b className="text-slate-700">{rc.to.join(", ")}</b></span>
          {rc.cc.length > 0 && <span>CC <b className="text-slate-700">{rc.cc.join(", ")}</b></span>}
          <span data-testid="work-report-thread-count">This month's thread: {rc.sent_this_month} report{rc.sent_this_month === 1 ? "" : "s"} sent</span>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          {PERIODS.map(([v, l]) => (
            <button key={v} data-testid={`work-report-period-${v}`} onClick={() => setPeriod(v)}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${period === v ? "bg-white shadow-sm text-slate-900 font-semibold" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
          ))}
        </div>
        <Input data-testid="work-report-date" type="date" value={date} max={today()} onChange={e => setDate(e.target.value)} className="h-9 w-[160px]" />
        {data && <span data-testid="work-report-range" className="inline-flex items-center gap-1.5 text-sm text-slate-600"><CalendarDays className="w-4 h-4 text-orange-500" />{data.label}</span>}
      </div>

      {data && <ReportData data={data} />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FreeText testid="work-report-other" title="Other tasks" hint="One per line — e.g. Responded to 50+ emails, followed up on PAL status…" value={other} onChange={setOther} />
        <FreeText testid="work-report-blockers" title="Blockers" hint="One per line — anything slowing you down or needing management help" value={blockers} onChange={setBlockers} />
      </div>
    </div>
  );
}

function ReportData({ data }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
      <div className="flex items-center gap-2 text-xs text-slate-500"><Lock className="w-3.5 h-3.5" /> From the CRM · read-only</div>
      <div data-testid="work-report-counts" className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {data.counts.map(c => (
          <div key={c.label} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{c.label}</div>
            <div className="heading-font text-2xl font-bold text-slate-900">{String(c.count).padStart(2, "0")}</div>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 bg-slate-50">
            <tr>{["App ID", "Student", "College", "Program", "Partner", "Stage"].map(h => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.applications.map(a => (
              <tr key={a.id} data-testid={`work-report-app-${a.id}`} className="border-t border-slate-100">
                <td className="px-3 py-2 mono text-xs">{a.application_id}</td>
                <td className="px-3 py-2 font-medium">{a.student_name}</td>
                <td className="px-3 py-2">{a.college_code || a.college}</td>
                <td className="px-3 py-2">{a.program}</td>
                <td className="px-3 py-2">{a.partner_name}</td>
                <td className="px-3 py-2"><span className={`stage-badge ${stageClass(a.stage)}`}>{a.stage}</span></td>
              </tr>
            ))}
            {data.applications.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No applications in this period</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const FreeText = ({ testid, title, hint, value, onChange }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
    <div className="heading-font font-semibold text-slate-900">{title}</div>
    <Textarea data-testid={testid} value={value} onChange={e => onChange(e.target.value)} placeholder={hint} maxLength={5000} className="min-h-[140px] text-sm" />
  </div>
);
